<template>
  <div class="flex flex-col items-center justify-center gap-2">
    <h4 class="text-gray-500-dark">
      {{ title_survey }}
      <span
        v-if="show_title_fallback_indicator"
        class="ml-1 text-xs text-gray-400"
        v-tooltip="title_fallback_tooltip"
        data-test="survey-title-fallback"
      >
        {{ title_fallback_language.toUpperCase() }}
      </span>
    </h4>
    <Rating
      v-if="question_type === SurveyQuestion.NPS || question_type === SurveyQuestion.OPINION_SCALE"
      :default_value="score"
      :disabled="disabled"
      :question_type="question_type"
      @scoreSelected="scoreSelected"
    />
    <YesNo
      v-if="question_type === SurveyQuestion.YES_NO"
      :default_value="score"
      :disabled="disabled"
      @scoreSelected="scoreSelected"
    />
    <Satisfaction
      v-if="question_type === SurveyQuestion.SATISFACTION"
      :default_value="score"
      :disabled="disabled"
      @scoreSelected="scoreSelected"
    />
    <FixedChoice
      v-if="question_type === SurveyQuestion.FIXED_CHOICE"
      :fixed_options="fixed_options"
      :disabled="disabled"
      @scoreSelected="scoreSelected"
    />
    <b-field
      :label="
        question_type !== SurveyQuestion.OPEN_ENDED ? $t('survey.labels.comments') : $t('survey.labels.answerRequired')
      "
      class="w-full"
    >
      <b-input
        v-model="feedback"
        data-test="survey-comments-input"
        :disabled="disabled"
        type="textarea"
        :required="question_type === SurveyQuestion.OPEN_ENDED"
      />
    </b-field>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { mapGetters } from '@/store/lib/vue3helpers.js';
import Rating from '@/app/shared_components/surveys/Rating.vue';
import YesNo from '@/app/shared_components/surveys/YesNo.vue';
import Satisfaction from '@/app/shared_components/surveys/Satisfaction.vue';
import FixedChoice from '@/app/shared_components/surveys/FixedChoice.vue';
import { SurveyQuestion } from '@/app/data/model_constants';
import { FT } from '@/app/data/flipper_constants';

const props = defineProps({
  disabled: { type: Boolean },
  title: { type: String, default: null },
  title_fallback_language: { type: String, default: null },
  question_type: { type: String, default: null },
  question_number: { type: Number, default: null },
  questions: { type: Number, default: null },
  fixed_options: { type: Array, default: null },
});

const emit = defineEmits(['feedbackUpdated', 'scoreSelected']);

const { proxy } = getCurrentInstance();
const { t } = useI18n();
const { current_client } = mapGetters(['current_client']);

const score = ref(null);
const feedback = ref(null);

const title_survey = computed(() => {
  if (props.question_type === SurveyQuestion.NPS) {
    return t('survey.labels.NPSQuestion', { name: current_client.value.attributes.name });
  }
  if (props.title) return props.title;
  return '...';
});

const show_title_fallback_indicator = computed(
  () =>
    props.question_type !== SurveyQuestion.NPS &&
    Boolean(props.title_fallback_language) &&
    proxy.$flipper.enabled(FT.MULTILINGUAL),
);

const title_fallback_tooltip = computed(() => {
  if (!props.title_fallback_language) return null;
  return t('constants.languages.fallback_info', {
    fallback_locale: t(`constants.languages.${props.title_fallback_language}`),
  });
});

const scoreSelected = (newScore) => {
  emit('scoreSelected', newScore);
  score.value = newScore;
};

watch(
  () => props.title,
  () => {
    score.value = null;
    feedback.value = null;
  },
);

watch(feedback, () => {
  emit('feedbackUpdated', feedback.value);
  if (props.question_type === SurveyQuestion.OPEN_ENDED) {
    scoreSelected(1);
  }
});
</script>
